import { useEffect, useMemo, useState } from 'react'
import { useSignIn, useUser } from '@clerk/clerk-react'
import type { PhoneCodeFactor } from '@clerk/types'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'

// COMPONENTS
import Button from '@components/atoms/button'
import TextField from '@components/atoms/textfield'
import ReferralCard from '@components/molecules/referral-card'
import { Toaster } from '@components/shadcn/sonner'
import { Column } from '@components/templates/column'

// UTILITIES
import { links } from '@constants'
import { useInput } from '@hooks/inputs'
import { formatInput, normalizeInput } from '@utils/phone-number-helper'
import { toast } from 'sonner'

// ROUTES
import { useAuthSettings } from '../_localStore/_hooks'
import { AuthIntroScreen } from './_components/auth-intro-screen'

import './sign-in.css'

type SignInView = 'intro' | 'form'

interface SignInLocationState {
    initialView?: SignInView
}

const blockedSignupMessage = 'We are not accepting new accounts at this time.'
const inviteOnlyNotice = 'We are not accepting new accounts at this time.'
const authSignInToasterId = 'auth-sign-in'

const placeholder = '+1 (555) 555-5555'
const maxPhoneLength = placeholder.length
const formId = 'auth-sign-in-form'

function parseSignInLocationState(state: unknown): { initialView: SignInView } {
    if (typeof state !== 'object' || state == null) {
        return { initialView: 'intro' }
    }

    const locationState = state as { initialView?: unknown }

    return {
        initialView: locationState.initialView === 'form' ? 'form' : 'intro',
    }
}

function isIdentifierNotFoundError(error: unknown): boolean {
    if (typeof error !== 'object' || error == null || !('errors' in error)) return false

    const errors = (error as { errors?: Array<{ code?: string }> }).errors
    return Array.isArray(errors) && errors[0]?.code === 'form_identifier_not_found'
}

function getErrorMessage(error: unknown, defaultMessage: string): string {
    if (typeof error !== 'object' || error == null) return defaultMessage

    const source = ('errors' in error)
        ? error as { errors?: Array<{ longMessage?: string }> }
        : ('error' in error && typeof error.error === 'object' && error.error != null)
            ? error.error as { errors?: Array<{ longMessage?: string }> }
            : null

    if (source == null) return defaultMessage

    const errors = source.errors
    if (!Array.isArray(errors)) return defaultMessage

    return errors[0]?.longMessage || defaultMessage
}

const AuthSignInScreen = () => {
    const { setIsNewUser } = useAuthSettings()
    const { isLoaded, isSignedIn } = useUser()
    const { signIn } = useSignIn()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()

    const [view, setView] = useState<SignInView>(() => parseSignInLocationState(location.state).initialView)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [countryCode] = useState('+1')
    const [phoneNumber, phoneNumberProps] = useInput({
        onChange: (value) => formatInput(value, phoneNumber)
    })

    const hasReferralId = useMemo(() => (
        !!searchParams.get('referralId')
    ), [searchParams])

    useEffect(() => {
        const state = parseSignInLocationState(location.state)
        setView(state.initialView)
    }, [location.key, location.state])

    // Redirect already-signed-in users to /account
    // This handles the edge case where Clerk's redirect fails due to capacitor:// protocol
    // and redirects to /sign-in, preventing a login loop.
    useEffect(() => {
        if (isLoaded && isSignedIn) {
            navigate('/account', { replace: true })
        }
    }, [isLoaded, isSignedIn, navigate])

    function showSignInForm() {
        setView('form')
    }

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        try {
            event.preventDefault()
            if (!isLoaded) throw new Error('Clerk has yet to load')
            if (isSubmitting) return
            setIsSubmitting(true)

            // If already signed in, redirect instead of signing out.
            if (isSignedIn) {
                navigate('/account', { replace: true })
                return
            }

            const signInRes = await attemptSignIn()
            const isSignInResource = typeof signInRes === 'object' && 'supportedFirstFactors' in signInRes
            const phoneNumber = isSignInResource
                ? signInRes.supportedFirstFactors?.find((factor) => factor.strategy === 'phone_code' && factor.primary) as PhoneCodeFactor | undefined
                : undefined

            if (isSignInResource && phoneNumber != null) {
                await signInRes.prepareFirstFactor({
                    strategy: 'phone_code',
                    phoneNumberId: phoneNumber.phoneNumberId
                })
                setIsNewUser(false)
                const search = document.location.search
                navigate(`/verify${search}`)
            } else throw signInRes
        } catch (error: unknown) {
            const state: SignInLocationState = {
                initialView: 'form',
            }

            navigate(`/sign-in${document.location.search}`, {
                replace: true,
                state,
            })

            const defaultMsg = 'An issue occured while Authenticating your account.'
            toast.error(getErrorMessage(error, defaultMsg), {
                toasterId: authSignInToasterId,
                duration: 5000,
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    async function attemptSignIn() {
        try {
            if (signIn == null) return { event: 'error', error: { errors: [{ longMessage: 'Auth Services have yet to load.' }] } }
            const result = await signIn.create({ identifier: `${countryCode}${normalizeInput(phoneNumber)}` })

            if (result.status === 'complete' || result.status === 'needs_first_factor') return result
            else return { event: 'error', error: { errors: [{ longMessage: `Failed with status: ${result.status}` }] } }
        } catch (error: unknown) {
            if (isIdentifierNotFoundError(error)) return { event: 'error', error: { errors: [{ longMessage: blockedSignupMessage }] } }
            else return { event: 'error', error }
        }
    }

    const isSubmitable = useMemo(() => validateInputs(phoneNumberProps.value), [phoneNumberProps.value])
    const headerText = 'Sign In'
    const helperText = 'Enter your registered phone number to continue.'

    if (view === 'intro') {
        return (
            <>
                <Column.Content className='auth-sign-in-intro-host' padding='none'>
                    <AuthIntroScreen onSignIn={showSignInForm} />
                </Column.Content>
                <Column.Footer hidden />
            </>
        )
    }

    return (
        <>
            <Column.Content className='auth-sign-in-screen'>
                <img className='splitcard-logo' src='/splitcard/logo/white.png' />
                <div className='flex flex-col justify-center gap-4 w-full'>
                    <h1 style={{ lineHeight: 1.15, marginBottom: '0.25rem' }}>{headerText}</h1>
                    <p className='auth-sign-in-intent-helper'>{helperText}</p>
                    <div className='auth-sign-in-access-note'>
                        <p>Already have an account? Sign in to continue.</p>
                        <p>{inviteOnlyNotice}</p>
                    </div>
                    <div className='flex flex-col'>
                        <form id={formId} onSubmit={handleSubmit}>
                            <label htmlFor='phone-number' />
                            <TextField
                                {...phoneNumberProps}
                                type='tel'
                                autoComplete='tel'
                                autoCapitalize='off'
                                autoCorrect='off'
                                inputMode='tel'
                                id='phone-number'
                                name='phone-number'
                                aria-labelledby='phone-number'
                                maxLength={maxPhoneLength}
                                placeholder='Phone number'
                                divideElementLeft={true}
                                inlineElementLeft={<p><strong>+1</strong></p>}
                                required
                            />
                        </form>
                    </div>
                    <small style={{ marginTop: '1rem' }}>
                        <a onClick={() => window.open(links.contactUs, '_blank')}>Need Help?</a>
                    </small>
                </div>
                {hasReferralId && (
                    <ReferralCard
                        variant='signUpClaim'
                        className='auth-sign-in-referral-card'
                    />
                )}
                <small>
                    Message and data rates may apply. By continuing, you agree to
                    our <a onClick={() => window.open(links.termsOfUse, '_blank')}>Terms of Service</a> & <a onClick={() => window.open(links.privacyPolicy, '_blank')}>Privacy Policy</a>
                </small>
                <Toaster
                    id={authSignInToasterId}
                    richColors
                    position='top-center'
                    offset={{ top: 'calc(env(safe-area-inset-top) + 1rem)' }}
                    mobileOffset={{ top: 'calc(env(safe-area-inset-top) + 1rem)' }}
                    toastOptions={{
                        toasterId: authSignInToasterId,
                        className: 'auth-sign-in-sonner-toast',
                    }}
                />
                <div id='clerk-captcha'></div>
            </Column.Content>
            <Column.Footer>
                <Button theme='secondary' form={formId} style={{ width: '100%' }} type='submit' disabled={!isSubmitable} loading={isSubmitting}>Continue</Button>
            </Column.Footer>
        </>
    )
}

const validateInputs = (phone: string): boolean => (
    phone.length === placeholder.replace('+1 ', '').length
)

const SignInRoute: RouteObject = {
    path: 'sign-in',
    element: <AuthSignInScreen />,
}

export default SignInRoute
